import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import Sidebar from '../../components/layout/Sidebar';
import Header from '../../components/layout/Header';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { 
  Plus, 
  Megaphone,
  Bell,
  Mail,
  MessageSquare,
  Users,
  Send,
  Calendar,
  MoreHorizontal
} from 'lucide-react';
import { cn } from '../../lib/utils';

const CampaignsPage = () => {
  const { user } = useAuth();
  const { theme } = useTheme();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  const campaigns = [
    {
      id: 1,
      name: 'Offre de Noël',
      type: 'banner',
      target: 'clients',
      status: 'active',
      startDate: '2024-12-01',
      endDate: '2024-12-31'
    },
    {
      id: 2,
      name: 'Bienvenue nouveaux créatifs',
      type: 'push',
      target: 'creatives',
      status: 'draft',
      startDate: null,
      endDate: null
    }
  ];

  const campaignTypes = [
    { type: 'banner', label: 'Bannière', icon: Megaphone, color: 'bg-purple-100 text-purple-700' },
    { type: 'push', label: 'Push', icon: Bell, color: 'bg-blue-100 text-blue-700' },
    { type: 'email', label: 'Email', icon: Mail, color: 'bg-green-100 text-green-700' },
    { type: 'sms', label: 'SMS', icon: MessageSquare, color: 'bg-orange-100 text-orange-700' },
  ];

  return (
    <div className="min-h-screen bg-[#f8fafc]">
      <Sidebar 
        collapsed={collapsed} 
        setCollapsed={setCollapsed}
        mobileOpen={mobileOpen}
        setMobileOpen={setMobileOpen}
      />
      <Header collapsed={collapsed} setMobileOpen={setMobileOpen} />
      
      <main className={cn(
        "transition-all duration-300 px-6 md:px-8 pb-6 md:pb-8",
        collapsed ? "md:ml-20" : "md:ml-64"
      )}
        style={{ paddingTop: "130px" }}
      >
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-bold" style={{ color: theme.colors?.primary }}>
              Campagnes
            </h1>
            <p className="text-[#64748b] mt-1">
              Gérez vos campagnes de communication multi-canal
            </p>
          </div>
          
          <Button 
            style={{ backgroundColor: theme.colors?.accent, color: theme.colors?.primary }}
            className="font-bold"
          >
            <Plus className="w-4 h-4 mr-2" />
            Nouvelle campagne
          </Button>
        </div>

        {/* Campaign Types */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          {campaignTypes.map((ct) => (
            <Card key={ct.type} className="card card-hover cursor-pointer">
              <CardContent className="p-4 text-center">
                <div className={cn("w-12 h-12 rounded-xl mx-auto mb-3 flex items-center justify-center", ct.color)}>
                  <ct.icon className="w-6 h-6" />
                </div>
                <p className="font-medium" style={{ color: theme.colors?.primary }}>{ct.label}</p>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Campaigns List */}
        <Card className="card">
          <CardHeader>
            <CardTitle>Campagnes actives</CardTitle>
          </CardHeader>
          <CardContent>
            {campaigns.length === 0 ? (
              <div className="text-center py-12">
                <Megaphone className="w-16 h-16 mx-auto mb-4 text-[#64748b] opacity-50" />
                <h3 className="text-lg font-semibold mb-2" style={{ color: theme.colors?.primary }}>
                  Aucune campagne
                </h3>
                <p className="text-[#64748b]">
                  Créez votre première campagne de communication
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {campaigns.map((campaign) => (
                  <div key={campaign.id} className="flex items-center justify-between p-4 rounded-xl border border-[#e2e8f0]">
                    <div className="flex items-center gap-4">
                      <div className={cn(
                        "w-10 h-10 rounded-lg flex items-center justify-center",
                        campaignTypes.find(ct => ct.type === campaign.type)?.color
                      )}>
                        {React.createElement(
                          campaignTypes.find(ct => ct.type === campaign.type)?.icon || Megaphone,
                          { className: "w-5 h-5" }
                        )}
                      </div>
                      <div>
                        <h4 className="font-medium" style={{ color: theme.colors?.primary }}>
                          {campaign.name}
                        </h4>
                        <p className="text-sm text-[#64748b] flex items-center gap-2">
                          <Users className="w-3 h-3" />
                          {campaign.target === 'clients' ? 'Clients' : 'Créatifs'}
                          {campaign.startDate && (
                            <>
                              <span>•</span>
                              <Calendar className="w-3 h-3" />
                              {campaign.startDate} - {campaign.endDate}
                            </>
                          )}
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-3">
                      <Badge className={campaign.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'}>
                        {campaign.status === 'active' ? 'Active' : 'Brouillon'}
                      </Badge>
                      <Button variant="ghost" size="icon">
                        <MoreHorizontal className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default CampaignsPage;
